import { defineStore } from '@/utils/store'
import axios, { type AxiosError } from 'axios'
import React from 'react'
import { getResponseError } from '@apis'
import { createUUID } from '@/utils'
import i18n from '@/i18n'

type CloseFn = (options: { close?: () => void; isOnlyClose?: boolean }) => void | Promise<void>

// 自訂彈窗
export type AlertOptions<T = any> = {
  // 唯一彈窗，可避免重複顯示
  key?: string
  // 標題
  title?: string | React.ReactNode
  // 內容
  message?: string | React.ReactNode
  // 自訂：覆蓋 title message
  children?: ({ onClose, onConfirm }: { onClose: CloseFn; onConfirm: CloseFn }) => React.ReactNode
  // 啟用狀態
  enabled?: boolean
  // 點擊背景是否可關閉
  bgClose?: boolean
  // 樣式覆蓋
  classNames?: { bg?: string; body?: string; content?: string; title?: string; message?: string; footer?: string }
  // 是否顯示浮動 X 按鈕
  closeButton?: boolean
  // 關閉
  close?: string | boolean // 取消文字
  onClose?: CloseFn // 取消事件
  closeProps?: T // 自訂按鈕參數，但因為客製化無法指定型別
  // 確認
  confirm?: string | boolean // 確認文字
  onConfirm?: (options: { close?: () => void }) => void | Promise<void> // 確認事件
  confirmProps?: T // 自訂按鈕參數，但因為客製化無法指定型別
}

type State = {
  alerts: AlertOptions[]
}

/** Alert Store */
export const [useAlertStore, setAlertState] = defineStore<State>(
  {
    alerts: [],
  },
  'ALERT',
)

/** 新增彈窗 */
export const alerts = (alert: AlertOptions) => {
  if (alert?.key && useAlertStore.getState().alerts.some((item) => item?.key === alert.key)) return () => () => closeAlert(alert?.key)
  const key = alert?.key || createUUID()
  const close = () => closeAlert(key)
  // close 覆蓋
  const originClose = alert?.onClose
  if (originClose) alert.onClose = (payload = {}) => originClose({ close, ...payload })
  else alert.onClose = close
  // confirm 覆蓋
  const originConfirm = alert?.onConfirm
  if (originConfirm) alert.onConfirm = (payload = {}) => originConfirm({ close, ...payload })
  else if (originClose) alert.onConfirm = (payload = {}) => originClose({ close, ...payload })
  else alert.onConfirm = close
  /** Add */
  setAlertState((state) => {
    state.alerts.push({
      key,
      title: i18n.t('old:') ?? '',
      ...alert,
      enabled: true,
    })
  })
  return () => close?.()
}

/** 自動套用 i18n 翻譯 */
export const alertI18n = (alert: AlertOptions) => {
  if (alert.title) alert.title = i18n?.t(String(alert.title))
  if (alert.message) alert.message = i18n?.t(String(alert.message))
  alerts(alert)
}

/** 自動確認 catch error 是否有訊息 */
export const errorAlert = (error: AxiosError<any> | unknown) => {
  if (typeof error !== 'object' || error === null) return // 不是物件
  if (axios.isCancel(error) as unknown) return // 被取消
  alerts({ message: getResponseError(error, String(error)) })
}

/** 關閉第一筆彈窗 */
export const closeAlert = (key?: string | React.MouseEvent | React.TouchEvent) => {
  if (typeof key !== 'string') key = ''
  setAlertState((state) => {
    if (!state.alerts?.length) return
    if (!key) state.alerts[0].enabled = false
    else {
      const index = state.alerts.findIndex((item) => item.key === key)
      if (index > -1) state.alerts[index].enabled = false
    }
  })
}

/** 刪除第一筆彈窗 */
export const shiftAlert = (key?: string | React.MouseEvent | React.TouchEvent) => {
  if (typeof key !== 'string') key = ''
  setAlertState((state) => {
    if (!state.alerts?.length) return
    if (!key) state.alerts.shift()
    else {
      const index = state.alerts.findIndex((item) => item.key === key)
      if (index > -1) state.alerts.splice(index, 1)
    }
  })
}
